import React, { useLayoutEffect, useRef, useState, Fragment } from "react";
import { TableProps, TableGroup, TableColumn, WithId } from "../app.interface";
import {
  renderTableCell,
  renderSortHeader,
  classNames,
} from "../utils/renderer/renderrer";
import {
  Icon,
  IconButton,
  Tooltip,
  alpha,
  Skeleton,
  TablePagination,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Paper,
  Typography,
  Box,
  Button,
} from "@mui/material";
import { TableToolbarActions } from "../app.interface";
import { AppExportDialog } from "./AppExportDialog";

// Base text styles
const baseTextStyles = "font-['Montserrat'] text-sm";
const baseLabelStyles = "font-['Montserrat'] text-sm font-medium";
const baseHeaderStyles = "font-['Montserrat'] text-base font-semibold";

const TableToolbar: React.FC<{
  actions?: TableToolbarActions;
  onExport: () => void;
}> = ({ actions, onExport }) => {
  const renderActionButton = (
    action: { label: string; is_disabled?: boolean; onClick?: () => void },
    icon: string
  ) => (
    <Tooltip title={action.label} arrow placement="top">
      <span>
        <IconButton
          size="small"
          onClick={action.onClick}
          disabled={action.is_disabled}
          sx={{
            width: 40,
            height: 40,
            bgcolor: (theme) => alpha("#0891B2", 0.08),
            color: "#0891B2",
            backdropFilter: "blur(8px)",
            boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              transform: "translateY(-1px)",
              bgcolor: (theme) => alpha("#0891B2", 0.12),
              boxShadow: "0 4px 8px rgba(0,0,0,0.08)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
          }}
        >
          <Icon sx={{ fontSize: 20 }}>{icon}</Icon>
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <div className="flex items-center gap-3">
      {actions?.custom?.map((action, index) =>
        renderActionButton(action, "more_horiz")
      )}
      {actions?.refresh && renderActionButton(actions.refresh, "refresh")}
      {actions?.export &&
        renderActionButton(
          {
            ...actions.export,
            onClick: onExport,
          },
          "download"
        )}
    </div>
  );
};

interface TableSkeletonProps {
  selectable?: boolean;
  columns: number;
}

const TableSkeleton: React.FC<TableSkeletonProps> = ({
  selectable,
  columns,
}) => {
  return (
    <>
      {Array.from({ length: 5 }).map((_, index) => (
        <TableRow key={index}>
          {selectable && (
            <td className="w-12 px-3 py-3">
              <Skeleton variant="rectangular" width={16} height={16} />
            </td>
          )}
          {Array.from({ length: columns }).map((_, colIndex) => (
            <td key={colIndex} className="px-3 py-3">
              <Skeleton variant="text" />
            </td>
          ))}
        </TableRow>
      ))}
    </>
  );
};

const EmptyView: React.FC<{
  icon?: string;
  title?: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
}> = ({ icon = "inventory_2", title, description, actionLabel, onAction }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      py: 8,
      px: 2,
      textAlign: "center",
    }}
  >
    <Box
      sx={{
        width: 80,
        height: 80,
        borderRadius: "50%",
        bgcolor: alpha("#0891B2", 0.08),
        color: "#0891B2",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mb: 2,
      }}
    >
      <Icon sx={{ fontSize: 40 }}>{icon}</Icon>
    </Box>
    <Typography
      variant="h6"
      sx={{
        fontFamily: "Montserrat",
        fontWeight: 600,
        color: "text.primary",
        mb: 1,
      }}
    >
      {title || "No Data Available"}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontFamily: "Montserrat",
        color: "text.secondary",
        maxWidth: 400,
        mb: actionLabel ? 3 : 0,
      }}
    >
      {description || "There are no items to display at the moment."}
    </Typography>
    {actionLabel && onAction && (
      <Button
        variant="outlined"
        startIcon={<Icon>add</Icon>}
        onClick={onAction}
        sx={{
          borderColor: "#0891B2",
          color: "#0891B2",
          "&:hover": {
            borderColor: "#0E7490",
            bgcolor: alpha("#0891B2", 0.04),
          },
        }}
      >
        {actionLabel}
      </Button>
    )}
  </Box>
);

export const AppTable: React.FC<TableProps> = ({
  data,
  columns,
  selectable = false,
  onSelectionChange,
  isGrouped = false,
  sortConfig,
  onSort,
  variant = "default",
  allowHeaderWrap = false,
  header,
  styleOptions = {},
  toolbarActions,
  loading = false,
  pagination,
  onRowClick,
  rowProps,
  emptyState,
}) => {
  const { bordered = false, columnSeparators = false } = styleOptions;
  const checkbox = useRef<HTMLInputElement>(null);
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const [selectedItems, setSelectedItems] = useState<
    (WithId & Record<string, any>)[]
  >([]);
  const [isExportOpen, setIsExportOpen] = useState(false);

  const typedData = data as (WithId & Record<string, any>)[];
  const groups = isGrouped ? (typedData as unknown as TableGroup[]) : null;
  const flatData = isGrouped ? [] : typedData;

  useLayoutEffect(() => {
    if (!selectable) return;
    const items = isGrouped
      ? (typedData as unknown as TableGroup[]).flatMap(
          (g) => g.items as (WithId & Record<string, any>)[]
        )
      : typedData;
    const isIndeterminate =
      selectedItems.length > 0 && selectedItems.length < items.length;
    setChecked(selectedItems.length === items.length);
    setIndeterminate(isIndeterminate);
    if (checkbox.current) {
      checkbox.current.indeterminate = isIndeterminate;
    }
  }, [selectedItems, typedData, isGrouped]);

  function toggleAll() {
    const items = isGrouped
      ? (typedData as unknown as TableGroup[]).flatMap(
          (g) => g.items as (WithId & Record<string, any>)[]
        )
      : typedData;
    setSelectedItems(checked || indeterminate ? [] : items);
    setChecked(!checked && !indeterminate);
    setIndeterminate(false);
    onSelectionChange?.(checked || indeterminate ? [] : items);
  }

  const handleSort = (column: TableColumn) => {
    if (onSort) {
      const sortField = column.filterField || column.field;
      const direction = sortConfig?.direction === "asc" ? "desc" : "asc";
      onSort(sortField, direction);
    }
  };

  const handleSelect = (row: WithId & Record<string, any>) => {
    const selectedIndex = selectedItems.findIndex(
      (item) => item._id === row._id
    );
    let newSelected: (WithId & Record<string, any>)[] = [];

    if (selectedIndex === -1) {
      newSelected = [...selectedItems, row];
    } else {
      newSelected = selectedItems.filter((item) => item._id !== row._id);
    }

    setSelectedItems(newSelected);
    onSelectionChange?.(newSelected);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {(header || toolbarActions) && (
        <div className="sm:flex sm:items-center sm:justify-between mb-4">
          {header && (
            <div className="sm:flex-auto">
              {header.title &&
                (loading ? (
                  <Skeleton variant="text" width={200} height={32} />
                ) : (
                  <h1 className={classNames(baseHeaderStyles, "text-gray-900")}>
                    {header.title}
                  </h1>
                ))}
              {header.subtitle &&
                (loading ? (
                  <Skeleton
                    variant="text"
                    width={300}
                    height={24}
                    className="mt-2"
                  />
                ) : (
                  <p
                    className={classNames(baseTextStyles, "mt-2 text-gray-700")}
                  >
                    {header.subtitle}
                  </p>
                ))}
            </div>
          )}
          <div className="flex items-center gap-4 mt-4 sm:mt-0 sm:ml-auto">
            {toolbarActions && (
              <TableToolbar
                actions={toolbarActions}
                onExport={() => setIsExportOpen(true)}
              />
            )}
            {header?.action &&
              (loading ? (
                <Skeleton variant="rounded" width={120} height={40} />
              ) : (
                <button
                  type="button"
                  onClick={header.action.onClick}
                  className={classNames(
                    baseLabelStyles,
                    "block rounded-md bg-indigo-600 px-3 py-1.5 text-center text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  )}
                >
                  {header.action.icon && (
                    <span className="mr-2">
                      <Icon>{header.action.icon}</Icon>
                    </span>
                  )}
                  {header.action.label}
                </button>
              ))}
          </div>
        </div>
      )}
      <div className={classNames("flow-root", bordered ? "sm:rounded-lg" : "")}>
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div
            className={classNames(
              "inline-block min-w-full py-2 align-middle",
              bordered ? "sm:px-6 lg:px-8" : ""
            )}
          >
            <div
              className={classNames(
                bordered
                  ? "ring-1 ring-gray-300 sm:rounded-lg overflow-hidden"
                  : ""
              )}
            >
              {loading ? (
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr
                      className={classNames(
                        "bg-gradient-to-r from-gray-50 to-white",
                        columnSeparators ? "divide-x divide-gray-200" : ""
                      )}
                    >
                      {selectable && (
                        <th
                          scope="col"
                          className="relative px-7 sm:w-12 sm:px-6"
                        >
                          <input
                            type="checkbox"
                            className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                            ref={checkbox}
                            checked={checked}
                            onChange={toggleAll}
                            disabled={loading}
                          />
                        </th>
                      )}
                      {columns.map((column) => (
                        <th
                          key={column.id}
                          scope="col"
                          className={classNames(
                            baseLabelStyles,
                            "py-4 text-gray-600 border-b-2 border-gray-200",
                            column.align === "right"
                              ? "text-right"
                              : "text-left",
                            variant === "compact" ? "px-2" : "px-4",
                            allowHeaderWrap ? "" : "whitespace-nowrap",
                            column.width || "",
                            "transition-colors hover:text-gray-900"
                          )}
                        >
                          <div
                            className={classNames(
                              "flex items-center gap-2",
                              allowHeaderWrap
                                ? ""
                                : "overflow-hidden text-ellipsis",
                              column.sortable && !loading
                                ? "cursor-pointer"
                                : ""
                            )}
                            onClick={() =>
                              column.sortable && handleSort(column)
                            }
                          >
                            {loading ? (
                              <Skeleton
                                variant="text"
                                width={100}
                                height={24}
                              />
                            ) : column.sortable ? (
                              renderSortHeader(
                                {
                                  ...column,
                                  field: column.filterField || column.field,
                                },
                                sortConfig,
                                onSort
                              )
                            ) : (
                              !column.hideHeader && (
                                <div className="flex items-center gap-2">
                                  <span
                                    title={column.header}
                                    className="font-semibold tracking-wide uppercase text-xs"
                                  >
                                    {column.header}
                                  </span>
                                  {!column.sortable && (
                                    <>
                                      {column.type === "datetime" && (
                                        <Icon
                                          fontSize="small"
                                          className="text-gray-400"
                                        >
                                          schedule
                                        </Icon>
                                      )}
                                      {column.type === "money" && (
                                        <Icon
                                          fontSize="small"
                                          className="text-gray-400"
                                        >
                                          attach_money
                                        </Icon>
                                      )}
                                      {column.type === "status" && (
                                        <Icon
                                          fontSize="small"
                                          className="text-gray-400"
                                        >
                                          fiber_manual_record
                                        </Icon>
                                      )}
                                    </>
                                  )}
                                </div>
                              )
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <TableBody>
                    <TableSkeleton
                      columns={columns.length}
                      selectable={selectable}
                    />
                  </TableBody>
                </table>
              ) : !groups && (!data || data.length === 0) ? (
                <EmptyView
                  icon={emptyState?.icon}
                  title={emptyState?.title}
                  description={emptyState?.description}
                  actionLabel={emptyState?.actionLabel}
                  onAction={emptyState?.onAction}
                />
              ) : (
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr
                      className={classNames(
                        "bg-gradient-to-r from-gray-50 to-white",
                        columnSeparators ? "divide-x divide-gray-200" : ""
                      )}
                    >
                      {selectable && (
                        <th
                          scope="col"
                          className="relative px-7 sm:w-12 sm:px-6"
                        >
                          <input
                            type="checkbox"
                            className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                            ref={checkbox}
                            checked={checked}
                            onChange={toggleAll}
                            disabled={loading}
                          />
                        </th>
                      )}
                      {columns.map((column) => (
                        <th
                          key={column.id}
                          scope="col"
                          className={classNames(
                            baseLabelStyles,
                            "py-4 text-gray-600 border-b-2 border-gray-200",
                            column.align === "right"
                              ? "text-right"
                              : "text-left",
                            variant === "compact" ? "px-2" : "px-4",
                            allowHeaderWrap ? "" : "whitespace-nowrap",
                            column.width || "",
                            "transition-colors hover:text-gray-900"
                          )}
                        >
                          <div
                            className={classNames(
                              "flex items-center gap-2",
                              allowHeaderWrap
                                ? ""
                                : "overflow-hidden text-ellipsis",
                              column.sortable && !loading
                                ? "cursor-pointer"
                                : ""
                            )}
                            onClick={() =>
                              column.sortable && handleSort(column)
                            }
                          >
                            {loading ? (
                              <Skeleton
                                variant="text"
                                width={100}
                                height={24}
                              />
                            ) : column.sortable ? (
                              renderSortHeader(
                                {
                                  ...column,
                                  field: column.filterField || column.field,
                                },
                                sortConfig,
                                onSort
                              )
                            ) : (
                              !column.hideHeader && (
                                <div className="flex items-center gap-2">
                                  <span
                                    title={column.header}
                                    className="font-semibold tracking-wide uppercase text-xs"
                                  >
                                    {column.header}
                                  </span>
                                  {!column.sortable && (
                                    <>
                                      {column.type === "datetime" && (
                                        <Icon
                                          fontSize="small"
                                          className="text-gray-400"
                                        >
                                          schedule
                                        </Icon>
                                      )}
                                      {column.type === "money" && (
                                        <Icon
                                          fontSize="small"
                                          className="text-gray-400"
                                        >
                                          attach_money
                                        </Icon>
                                      )}
                                      {column.type === "status" && (
                                        <Icon
                                          fontSize="small"
                                          className="text-gray-400"
                                        >
                                          fiber_manual_record
                                        </Icon>
                                      )}
                                    </>
                                  )}
                                </div>
                              )
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <TableBody>
                    {groups
                      ? groups.map((group, groupIdx) => (
                          <Fragment key={groupIdx}>
                            <tr className="bg-gray-50">
                              <td
                                colSpan={columns.length + (selectable ? 1 : 0)}
                                className="py-2 px-3 text-sm font-medium text-gray-900"
                              >
                                {group.name}
                              </td>
                            </tr>
                            {group.items.map((row, rowIdx) => {
                              const typedRow = row as WithId &
                                Record<string, any>;
                              return (
                                <TableRow
                                  key={typedRow._id || rowIdx}
                                  hover
                                  onClick={(e) => {
                                    if (
                                      (e.target as HTMLElement).closest(
                                        'button, input[type="checkbox"]'
                                      )
                                    ) {
                                      return;
                                    }
                                    onRowClick?.(typedRow);
                                  }}
                                  sx={{
                                    cursor: onRowClick ? "pointer" : "default",
                                    ...rowProps?.sx,
                                  }}
                                >
                                  {selectable && (
                                    <td className="relative px-7 sm:w-12 sm:px-6">
                                      <input
                                        type="checkbox"
                                        className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                        value={rowIdx}
                                        checked={selectedItems.some(
                                          (item) => item._id === typedRow._id
                                        )}
                                        onChange={(e) => {
                                          const newSelectedItems = e.target
                                            .checked
                                            ? [...selectedItems, typedRow]
                                            : selectedItems.filter(
                                                (item) =>
                                                  item._id !== typedRow._id
                                              );
                                          setSelectedItems(newSelectedItems);
                                          onSelectionChange?.(newSelectedItems);
                                        }}
                                      />
                                    </td>
                                  )}
                                  {columns.map((column) => (
                                    <td
                                      key={column.id}
                                      className={classNames(
                                        baseTextStyles,
                                        "whitespace-nowrap",
                                        variant === "compact"
                                          ? "px-2 py-2"
                                          : "px-3 py-4",
                                        column.align === "right"
                                          ? "text-right"
                                          : "text-left"
                                      )}
                                    >
                                      {renderTableCell(typedRow, column)}
                                    </td>
                                  ))}
                                </TableRow>
                              );
                            })}
                          </Fragment>
                        ))
                      : flatData.map((row, rowIdx) => {
                          const typedRow = row as WithId & Record<string, any>;
                          return (
                            <TableRow
                              hover
                              key={`row-${rowIdx}`}
                              onClick={(e) => {
                                if (
                                  (e.target as HTMLElement).closest(
                                    'button, input[type="checkbox"]'
                                  )
                                ) {
                                  return;
                                }
                                onRowClick?.(typedRow);
                              }}
                              sx={{
                                cursor: onRowClick ? "pointer" : "default",
                                ...rowProps?.sx,
                              }}
                            >
                              {selectable && (
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    color="primary"
                                    checked={selectedItems.some(
                                      (item) => item._id === typedRow._id
                                    )}
                                    onChange={() => handleSelect(typedRow)}
                                    inputProps={{
                                      "aria-labelledby": `enhanced-table-checkbox-${rowIdx}`,
                                    }}
                                  />
                                </TableCell>
                              )}
                              {columns.map((column, colIdx) => (
                                <TableCell
                                  key={`cell-${rowIdx}-${colIdx}`}
                                  align={column.align || "left"}
                                >
                                  {renderTableCell(typedRow, column)}
                                </TableCell>
                              ))}
                            </TableRow>
                          );
                        })}
                  </TableBody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
      {pagination && (
        <div className="mt-4 flex justify-end">
          <TablePagination
            component="div"
            count={pagination.total}
            page={pagination.page}
            onPageChange={(_, newPage) => pagination.onPageChange(newPage)}
            rowsPerPage={pagination.pageSize}
            onRowsPerPageChange={(e) =>
              pagination.onPageSizeChange(parseInt(e.target.value, 10))
            }
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              ".MuiTablePagination-select": {
                fontFamily: "Montserrat",
              },
              ".MuiTablePagination-displayedRows": {
                fontFamily: "Montserrat",
              },
            }}
          />
        </div>
      )}
      {toolbarActions?.export && (
        <AppExportDialog
          open={isExportOpen}
          onClose={() => setIsExportOpen(false)}
          formats={
            toolbarActions.export.options?.formats || ["csv", "excel", "json"]
          }
          totalItems={flatData.length}
          selectedItems={selectedItems.map((_, index) => index)}
          data={flatData}
          columns={columns}
          filename={toolbarActions.export.options?.filename || "table-export"}
        />
      )}
    </div>
  );
};
